/**
 * Accordions
 * 
 * # Structure of js-accordion
    <div id="{{Accordion.id}}" class="js-accordion">
        <div 
            id="{{AccordionItem.id}}" 
            class="js-accordion-item collapsible collapsible_collapsed">
            <a href="#{{AccordionItem.id}}" class="js-accordion-link">
                <span>{{ Title }}</span> 
                <span class="collapsible-marker"></span>
            </a>
            <div class="collapsible-content">
                {{ Content }}
            </div>
        </div>
    </div>
 *  
 * HTML authors need to do the following:
 * * Mark all elements that are "accordion items" (something that expands/collapses) with class="js-accordion-item"
 * * Give all accordion items a unique client ID.
 * * Wrap all "accordion items" within in one parent element with class="js-accordion".
 * * Use <a href="#{{js-accordion-item ID}}" class="js-accordion-link"> to toggle expand and collapse.
 * * Don't forget the class="js-accordion-link" on those <a> elements.
 * 
 * CSS hooks are these:
 *     .collapsible, .collapsible_* (See below.) 
 * Those classes should go on the same element with the "js-accordion-item" class.
 */

const cssClasses = {
    accordion: 'js-accordion',
    accordionItem: 'js-accordion-item',
    accordionLink: 'js-accordion-link',
    accordionLinkExpanded: 'collapsible-link_expanded',
    accordionAutoOpen: 'js-accordion_open',

    collapsible: 'collapsible',
    collapsibleStates: {
        expanding: 'collapsible_expanding',
        expanded: 'collapsible_expanded',
        collapsing: 'collapsible_collapsing',
        collapsed: 'collapsible_collapsed',
    },
};

const customHrefAttribute = 'href';

class AccordionItem {
    constructor(element, parentKey, expandedItems, canExpand) {
        this._element = element;
        this._parentKey = parentKey;
        this._expandedItems = expandedItems;
        this._init(canExpand);
    }

    get isExpanded() {
        return this._isExpanded;
    }

    get element() {
        return this._element;
    }

    get parentKey() {
        return this._parentKey;
    }

    collapse(isInitialState) {
        this._removeCssStateStyles();
        this.element.classList.add(cssClasses.collapsibleStates.collapsed);
        this._isExpanded = false;
        if (!isInitialState) {
            this._expandedItems.set(this._parentKey, '');
        }
        this._collapseLinks(`#${this.element.id}`);
    }

    expand() {
        this._removeCssStateStyles();
        this.element.classList.add(cssClasses.collapsibleStates.expanded);
        this._isExpanded = true;
        this._expandedItems.set(this._parentKey, `#${this.element.id}`);
        this._expandLinks(`#${this.element.id}`);
    }

    // AEGCLIEN-150: Special case for Claims table.
    _collapseLinks(hash) {
        // TODO: Optimize this lookup by caching it with the other items.
        let elements;

        elements = document.querySelectorAll(`[data-href="${hash}"]`);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.classList.remove(cssClasses.accordionLinkExpanded);
        }

        elements = document.querySelectorAll(`a[href="${hash}"]`);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.classList.remove(cssClasses.accordionLinkExpanded);
        }
    }

    _expandLinks(hash) {
        // TODO: Optimize this lookup by caching it with the other items.
        let elements;

        elements = document.querySelectorAll(`[data-href="${hash}"]`);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.classList.add(cssClasses.accordionLinkExpanded);
        }

        elements = document.querySelectorAll(`a[href="${hash}"]`);
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            element.classList.add(cssClasses.accordionLinkExpanded);
        }
    }
    // /AEGCLIEN-150

    _init(canExpand) {
        // Ensure only one item in the accordion is expanded.
        if (canExpand &&
            this.element.classList.contains(cssClasses.collapsibleStates.expanded)) {
            this.expand(true);
        } else {
            this.collapse(true);
        }
    }

    _removeCssStateStyles() {
        this.element.classList.remove(cssClasses.collapsibleStates.collapsing);
        this.element.classList.remove(cssClasses.collapsibleStates.collapsed);
        this.element.classList.remove(cssClasses.collapsibleStates.expanding);
        this.element.classList.remove(cssClasses.collapsibleStates.expanded);
    }
}

export default class Accordions {
    constructor() {
        this._allAccordionItems = new Map();
        this._expandedItems = new Map();
        document.addEventListener('DOMContentLoaded', this._init.bind(this));
    }

    get allAccordionItems() {
        return this._allAccordionItems;
    }

    get expandedItems() {
        return this._expandedItems;
    }

    _extractHashFromUrl(url) {
        const strArray = url.split('#');
        let hash = '';
        if (strArray && strArray.length > 1) {
            hash = strArray[1];
        }
        return `#${hash}`;
    }

    _init() {
        const accordionElements = document.getElementsByClassName(cssClasses.accordion);

        if (!accordionElements || !accordionElements.length) {
            return;
        }

        let accordionsCount = 0;
        for (let i = 0; i < accordionElements.length; i++) {
            const accordionElement = accordionElements[i];
            const accordionItemElements = accordionElement.getElementsByClassName(cssClasses.accordionItem);

            if (!accordionElements || !accordionElements.length) {
                continue;
            }

            // Only increment the count if there were items in the accordion.
            accordionsCount++;

            let hasExpanded = false;
            for (let i = 0; i < accordionItemElements.length; i++) {
                const accordionItemElement = accordionItemElements[i];
                const accordionItem = new AccordionItem(
                    accordionItemElement,
                    `#${accordionElement.id}`,
                    this._expandedItems, false); // Don't do any initial expansion; ignore expanded CSS classes.
                    // Passing in a URL hash will still set the initial expansion.

                // Only one accordion item should be expanded at a time.
                if (!hasExpanded) {
                    hasExpanded = accordionItem.isExpanded;
                };

                this._allAccordionItems.set(`#${accordionItem.element.id}`, accordionItem);
            }
        }

        // Don't wire up the event handler if there are no accordions!
        if (accordionsCount === 0) {
            return;
        }

        window.addEventListener('hashchange', () => {
            this._updateUI(window.location.hash);
        });

        this._links = document.getElementsByClassName(cssClasses.accordionLink);

        for (let i = 0; i < this._links.length; i++) {
            const element = this._links[i];

            // Special case for when the element that is clickable is not an <a> element.
            if (element.tagName !== 'a' && element.dataset[customHrefAttribute]) {
                element.addEventListener('click', () => {
                    if (window.location.hash === element.dataset[customHrefAttribute]) {
                        this._updateUI(window.location.hash, true);
                    } else {
                        // window.location.hash = element.dataset[customHrefAttribute];
                        this._updateUI(element.dataset[customHrefAttribute]);
                    }
                });
            } else { // General case: Clickable element is <a>.
                element.addEventListener('click', () => {
                    const hrefHash = this._extractHashFromUrl(element.href);
                    if (window.location.hash === hrefHash) {
                        this._updateUI(hrefHash, true);
                    } else {
                        this._updateUI(hrefHash);
                    }
                    event.preventDefault();
                });
            }
        }

        this._updateUI(window.location.hash);
    }

    _updateUI(hash, isToggle) {
        // Get the new item's parent key.
        const newAccordionItem = this.allAccordionItems.get(hash);
        if (!newAccordionItem) {
            return;
        }

        const newParentKey = newAccordionItem.parentKey;

        // Get the current expanded item for that key.
        const oldExpandedKey = this.expandedItems.get(newParentKey);

        if (isToggle || hash === oldExpandedKey) {
            newAccordionItem.isExpanded ?
                newAccordionItem.collapse() : newAccordionItem.expand();
            return;
        }

        let oldExpanded;
        if (oldExpandedKey) {
            oldExpanded = this.allAccordionItems.get(oldExpandedKey);
        }

        // *** IMPORTANT! Always collapse first so state is set properly.

        if (oldExpanded) {
            // Get the old accordion item and collapse it.
            this.allAccordionItems.get(oldExpandedKey).collapse();
        }

        // Get the new accordion item and expand it.
        this.allAccordionItems.get(hash).expand();
    }
}
